import { useState, useEffect, useRef } from 'react';
import { testimonials } from '@/mocks/homeContent';

export default function TestimonialSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  const next = () => setActiveIndex((i) => (i + 1) % testimonials.length);
  const prev = () => setActiveIndex((i) => (i - 1 + testimonials.length) % testimonials.length);

  const t = testimonials[activeIndex];

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const reveals = el.querySelectorAll('.reveal');
            reveals.forEach((r) => r.classList.add('visible'));
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="testimonials" className="relative py-24 md:py-32 px-4 md:px-6 lg:px-12 bg-background-50">
      <div className="max-w-[1200px] mx-auto">
        {/* Header */}
        <div className="reveal text-center mb-16">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 tracking-tight">
            <span className="text-foreground-400 font-light">(</span>
            Trusted by growing businesses
            <span className="text-foreground-400 font-light">)</span>
          </h2>
        </div>

        {/* Testimonial card */}
        <div className="reveal reveal-delay-1 relative max-w-3xl mx-auto">
          <div className="bg-background-50 border border-background-200/60 rounded-2xl p-8 md:p-12">
            {/* Rating badge */}
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-foreground-950 text-white mb-8">
              <i className="ri-star-fill text-accent-500 text-xs w-3 h-3 flex items-center justify-center" />
              <span className="text-xs font-semibold">5.0</span>
            </div>

            <blockquote className="font-heading text-lg md:text-xl lg:text-2xl text-foreground-900 leading-relaxed italic relative">
              <span className="absolute -top-4 -left-2 text-5xl md:text-6xl text-primary-200 dark:text-primary-500/20 font-serif leading-none select-none">
                &ldquo;
              </span>
              {t.quote}
            </blockquote>

            {/* Author */}
            <div className="flex items-center gap-4 mt-8">
              <div className="w-12 h-12 rounded-full bg-[linear-gradient(135deg,oklch(var(--accent-400)/0.4),oklch(var(--primary-400)/0.3))] flex items-center justify-center">
                <span className="font-semibold text-sm text-background-50">{t.initials}</span>
              </div>
              <div>
                <div className="text-sm font-semibold text-foreground-950">{t.name}</div>
                <div className="text-xs text-foreground-500">{t.role}</div>
              </div>
            </div>
          </div>

          {/* Navigation arrows */}
          <div className="flex items-center justify-end gap-2 mt-6">
            <button
              onClick={prev}
              className="w-10 h-10 rounded-full border border-background-300 bg-background-50 flex items-center justify-center hover:bg-background-100 transition-colors duration-200 cursor-pointer"
              aria-label="Previous testimonial"
            >
              <i className="ri-arrow-left-line text-foreground-700 w-4 h-4 flex items-center justify-center" />
            </button>
            <button
              onClick={next}
              className="w-10 h-10 rounded-full bg-foreground-950 flex items-center justify-center hover:bg-foreground-800 transition-colors duration-200 cursor-pointer"
              aria-label="Next testimonial"
            >
              <i className="ri-arrow-right-line text-white w-4 h-4 flex items-center justify-center" />
            </button>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-1.5 mt-6">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveIndex(i)}
                className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                  i === activeIndex ? 'w-6 bg-primary-500' : 'w-2 bg-background-300 hover:bg-background-400'
                }`}
                aria-label={`Go to testimonial ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}